import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { removeFromCart } from "../../Redux/CartSlice";
import toast from "react-hot-toast";
import axios from "axios";

const CartPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { cartItems } = useSelector((state) => state.cartSlice);

  const [cartD, setCartD] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showLogin, setShowLogin] = useState(false); // login modal/section toggle
  const [guestData, setGuestData] = useState({
    name: "",
    email: "",
    mobile_no: "",
    locality: "",
    state: "",
    state_code: "",
    pincode: "",
    city: "",
    country: "",
    address_type: "",
  });

  // Initialize cart and login
  useEffect(() => {
    const newCartItems = cartItems?.map((item) => ({
      ...item,
      price: parseFloat(item.price) || 0,
      quantity: item.quantity || 1,
    }));
    setCartD(newCartItems);

    const token = localStorage.getItem("token");
    setIsLoggedIn(!!token);
  }, [cartItems]);

  // Calculate total
  useEffect(() => {
    const totalPrice = cartD.reduce(
      (acc, item) => acc + item.price * item.quantity,
      0
    );
    setTotal(totalPrice);
  }, [cartD]);

  // Delete from cart instantly
  const deleteFromCart = (item) => {
    dispatch(removeFromCart(item));
    setCartD((prev) => prev.filter((cartItem) => cartItem.id !== item.id));
    toast.success("Removed from cart");
  };

  // Guest login
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setGuestData((prev) => ({ ...prev, [name]: value }));
  };

  const guestLogin = (e) => {
    e.preventDefault();
    const id = toast.loading("Logging in as guest...");
    axios
      .post("https://admin.tulikaarts.in/api/guest-register", guestData)
      .then((response) => {
        localStorage.setItem("token", response.data.token);
        setIsLoggedIn(true);
        toast.success(response?.data?.message || "Guest login successful!", {
          id,
        });
      })
      .catch(() => {
        toast.error("Guest login failed", { id });
      });
  };

  return (
    <div>
      {/* Add top padding to avoid navbar overlap */}
      <div style={{ height: "80px" }}></div>

      <div className="container py-5">
        {/* HEADING */}
        <div className="text-center mb-5">
          <h1 className="fw-bold text-dark">🛒 Your Art Collection</h1>
          <p className="text-muted">
            Review your favorite paintings before proceeding to checkout
          </p>
          <hr
            className="mx-auto"
            style={{ width: "80px", border: "2px solid #000" }}
          />
        </div>

        <div className="row">
          {/* CART ITEMS */}
          <div className="col-lg-7 col-md-12 mb-4">
            {cartD.length > 0 ? (
              cartD.map((item) => (
                <div
                  key={item.id}
                  className="cart-item shadow-sm p-3 mb-4 bg-white rounded-4 d-flex align-items-center justify-content-between"
                >
                  <div className="d-flex align-items-center gap-3">
                    <img
                      src={item.image}
                      alt={item.title}
                      className="rounded"
                      style={{
                        width: "100px",
                        height: "100px",
                        objectFit: "cover",
                      }}
                    />
                    <div>
                      <h5 className="fw-semibold text-dark mb-1">
                        {item.title}
                      </h5>
                      <p className="text-muted mb-1">
                        <b>Size:</b> {item.size}
                      </p>
                      {/* ADDED: Display Print On selection */}
                      {item.print && (
                        <p className="text-muted mb-1">
                          <b>Print On:</b> {item.print}
                        </p>
                      )}
                      <p className="text-success fw-semibold mb-0">
                        ₹ {item.price}
                      </p>
                    </div>
                  </div>
                  <div className="text-end">
                    <button
                      onClick={() => deleteFromCart(item)}
                      className="btn text-white btn-sm rounded-pill px-4"
                    >
                      <i className="bi bi-trash"></i> Remove
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center text-muted py-5">
                <h5>Your cart is empty 🛍️</h5>
                <Link to="/" className="btn btn-dark mt-3 rounded-pill">
                  Continue Shopping
                </Link>
              </div>
            )}
          </div>

          {/* ORDER SUMMARY / GUEST / LOGIN */}
          <div className="col-lg-5 col-md-12">
            <div className="shadow-sm bg-white p-4 rounded-4">
              {isLoggedIn ? (
                <>
                  <h4 className="fw-bold mb-3 text-dark">Order Summary</h4>
                  <div className="d-flex justify-content-between mb-2">
                    <span>Total Items:</span>
                    <b>{cartD.length}</b>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span>Subtotal:</span>
                    <b>₹ {total}</b>
                  </div>
                  <div className="d-flex justify-content-between mb-3 border-top pt-2">
                    <span>Total Payable:</span>
                    <b className="text-success">₹ {total}</b>
                  </div>

                  <button
                    onClick={() => navigate("/checkout", { state: total })}
                    className="btn btn-dark w-100 rounded-pill py-2"
                  >
                    Proceed to Checkout →
                  </button>
                </>
              ) : (
                <>
                  {/* Login Button */}
                  <div className="text-end mb-3">
                    <Link
                      to="/login"
                      className="btn btn-outline-dark w-25 rounded-pill"
                    >
                      Login
                    </Link>
                  </div>

                  {/* Guest Checkout Form */}
                  <h4 className="fw-bold text-dark mb-3 text-center">
                    Guest Checkout
                  </h4>
                  <form onSubmit={guestLogin}>
                    <input
                      type="text"
                      name="name"
                      value={guestData.name}
                      onChange={handleInputChange}
                      className="form-control mb-2"
                      placeholder="Full Name"
                      required
                    />
                    <input
                      type="email"
                      name="email"
                      value={guestData.email}
                      onChange={handleInputChange}
                      className="form-control mb-2"
                      placeholder="Email"
                      required
                    />
                    <input
                      type="text"
                      name="mobile_no"
                      value={guestData.mobile_no}
                      onChange={handleInputChange}
                      className="form-control mb-2"
                      placeholder="Mobile Number"
                      required
                    />
                    <input
                      type="text"
                      name="locality"
                      value={guestData.locality}
                      onChange={handleInputChange}
                      className="form-control mb-2"
                      placeholder="Locality"
                      required
                    />
                    <input
                      type="text"
                      name="city"
                      value={guestData.city}
                      onChange={handleInputChange}
                      className="form-control mb-2"
                      placeholder="City"
                      required
                    />
                    <input
                      type="text"
                      name="state"
                      value={guestData.state}
                      onChange={handleInputChange}
                      className="form-control mb-2"
                      placeholder="State"
                      required
                    />
                    <input
                      type="text"
                      name="pincode"
                      value={guestData.pincode}
                      onChange={handleInputChange}
                      className="form-control mb-2"
                      placeholder="Pincode"
                      required
                    />
                    <select
                      name="address_type"
                      value={guestData.address_type}
                      onChange={handleInputChange}
                      className="form-control mb-3"
                      required
                    >
                      <option value="">Select Address Type</option>
                      <option value="home">Home</option>
                      <option value="office">Office</option>
                      <option value="other">Other</option>
                    </select>

                    <button
                      type="submit"
                      className="btn btn-dark w-100 rounded-pill py-2"
                    >
                      Continue as Guest
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;